import * as React from "react";

import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { Button } from "@replohq/lattice/components/button/Button";
import { Drawer } from "@replohq/lattice/components/drawer/Drawer";
import { Modal } from "@replohq/lattice/components/modal/Modal";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { getProductLabels } from "@v2/utils/project-labels";
import { filterNulls } from "replo-utils/lib/array";

type DeleteConfirmationModalProps = {
  onDelete: () => void;
  onRequestClose: () => void;
  isLoading?: boolean;
  assetName: string;
  assetTypeDisplayName: string;
  confirmationType?: "unpublish" | "delete";
  extraMessage?: React.ReactNode;
  showAssetName?: boolean;
  size?: "sm" | "base";
};

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  onDelete,
  onRequestClose,
  assetName,
  assetTypeDisplayName,
  isLoading,
  confirmationType = "delete",
  extraMessage,
  showAssetName = true,
  size = "base",
}) => {
  const isMobile = useIsMobile();
  const productLabels = getProductLabels();

  const title =
    confirmationType === "unpublish"
      ? `Are you sure you want to unpublish this ${assetTypeDisplayName}?`
      : `Are you sure you want to delete this ${assetTypeDisplayName}?`;
  const subtitle =
    confirmationType === "unpublish"
      ? `Unpublishing this ${assetTypeDisplayName} will remove it from all pages or templates where it has been added in the ${productLabels.shopifyThemeBuilder}.`
      : `By deleting this ${assetTypeDisplayName} you will remove it and all its
  contents from your account permanently.`;

  const description = filterNulls([subtitle, extraMessage]).join(" ");

  const footer = (
    <div className="flex justify-end w-full">
      <Button
        variant="destructive"
        size="base"
        onClick={onDelete}
        loading={isLoading}
        data-testid="delete-confirmation-button"
      >
        {confirmationType === "delete"
          ? `Delete ${assetTypeDisplayName}`
          : "I Understand"}
      </Button>
    </div>
  );

  const content = showAssetName ? (
    <div className="flex flex-col gap-y-2">
      <LabeledControl size="sm" label={`${assetTypeDisplayName} Name`}>
        <p className="text-default text-sm">{assetName}</p>
      </LabeledControl>
    </div>
  ) : undefined;

  if (isMobile) {
    return (
      <Drawer
        open={true}
        onOpenChange={onRequestClose}
        title={title}
        description={description}
        footer={footer}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      open={true}
      onOpenChange={onRequestClose}
      size={size}
      title={title}
      description={description}
      footer={footer}
    >
      {content}
    </Modal>
  );
};

export default DeleteConfirmationModal;
